import { useMemo, useState } from "react";
import { BookCheck, CalendarCheck, Check, EyeOff, Flame, Mic, PenLine, Send, Shield, type LucideIcon } from "lucide-react";
import { cn } from "../lib/format";
import { Dialog } from "./ui";

export type WeekMark = { number: number; starts_on: string; status: "valida" | "comodin" | "perdida" | "en_curso" };
export type Streak = { current: number; best: number; week_done: boolean; freeze_available?: boolean; weeks?: WeekMark[] };
export type BadgeKind = "primera_revision" | "primera_reescritura" | "primer_capitulo" | "voz_de_la_clase" | "constancia";
export type EarnedBadge = { kind: BadgeKind; earned_at: string | null; count: number };
export type WallMember = {
  id: string; full_name: string; tagline: string; is_me: boolean; visible: boolean; streak: Streak; badges: EarnedBadge[];
};
export type Wall = { group: { id: string; name: string }; me: string; members: WallMember[] };

/** Las insignias premian lo que mejora el texto, nunca el volumen. */
export const BADGES: Record<BadgeKind, { label: string; how: string; icon: LucideIcon; repeats?: boolean }> = {
  primera_revision: { label: "Primera revisión", how: "Envía una versión a revisión de tu profesor.", icon: Send },
  primera_reescritura: { label: "Reescritura", how: "Guarda una versión que atienda los comentarios de tu profesor.", icon: PenLine },
  primer_capitulo: { label: "Capítulo terminado", how: "Marca como terminado tu primer capítulo (300 palabras o más).", icon: BookCheck },
  voz_de_la_clase: { label: "Voz de la clase", how: "Sé quien más participa en una clase. Se gana en cada clase.", icon: Mic, repeats: true },
  constancia: { label: "Constancia", how: "Cumple tu meta 4 semanas seguidas. Se gana cada 4 semanas.", icon: CalendarCheck, repeats: true },
};
export const BADGE_ORDER: BadgeKind[] = ["primera_revision", "primera_reescritura", "primer_capitulo", "voz_de_la_clase", "constancia"];

export const weeksLabel = (n: number) => (n === 1 ? "semana" : "semanas");

/** Número grande de la racha con la flama en el naranja ELE. */
export function StreakNumber({ value, size = "lg" }: { value: number; size?: "lg" | "md" | "sm" }) {
  const on = value > 0;
  return (
    <span className={cn("inline-flex items-center", size === "lg" ? "gap-8" : "gap-[4px]")}>
      <Flame
        aria-hidden
        size={size === "lg" ? 28 : size === "md" ? 22 : 16}
        strokeWidth={size === "sm" ? 2 : 1.75}
        className={on ? "text-clay" : "text-mist"}
        fill={on ? "currentColor" : "none"}
        fillOpacity={on ? 0.18 : 0}
      />
      <span className={cn("font-serif tabular-nums text-carbon-ink", size === "lg" ? "text-[44px] leading-none" : size === "md" ? "text-[32px] leading-none" : "text-base16 font-medium")}>{value}</span>
    </span>
  );
}

/** Tira de semanas: cumplida, cubierta por comodín, perdida o en curso. */
export function WeekStrip({ weeks, className }: { weeks: WeekMark[]; className?: string }) {
  const label: Record<WeekMark["status"], string> = {
    valida: "cumplida", comodin: "cubierta con tu comodín", perdida: "sin avance", en_curso: "en curso",
  };
  return (
    <ol className={cn("flex flex-wrap gap-[6px]", className)} aria-label="Tus semanas">
      {weeks.map((w) => (
        <li key={w.number} title={`Semana ${w.number}: ${label[w.status]}`} aria-label={`Semana ${w.number}: ${label[w.status]}`}
          className={cn(
            "flex h-[26px] w-[26px] items-center justify-center rounded-[7px] text-[11px] tabular-nums",
            w.status === "valida" && "bg-carbon-ink text-bone-parchment",
            w.status === "comodin" && "border border-clay text-clay",
            w.status === "perdida" && "bg-soft-stone text-pebble",
            w.status === "en_curso" && "border border-dashed border-mist text-ashen",
          )}
        >
          {w.status === "comodin" ? <Shield size={13} strokeWidth={2} aria-hidden /> : w.number}
        </li>
      ))}
    </ol>
  );
}

/** Insignia ganada (o pendiente, en gris) con su contador cuando se repite. */
export function BadgeChip({ kind, count, earned = true, withLabel = false, small = false }: { kind: BadgeKind; count?: number; earned?: boolean; withLabel?: boolean; small?: boolean }) {
  const b = BADGES[kind];
  const Icon = b.icon;
  const title = earned ? `${b.label}${count && count > 1 ? ` x${count}` : ""}` : `${b.label}: ${b.how}`;
  return (
    <span title={title} aria-label={title}
      className={cn(
        "relative inline-flex shrink-0 items-center gap-[6px] rounded-full",
        withLabel ? "py-[3px] pl-[3px] pr-[10px] text-xs2" : "",
        earned ? (withLabel ? "bg-soft-stone text-carbon-ink" : "") : (withLabel ? "bg-paper-white text-pebble border border-chalk" : ""),
      )}
    >
      <span className={cn(
        "flex items-center justify-center rounded-full",
        small ? "h-[20px] w-[20px]" : "h-[26px] w-[26px]",
        earned ? "bg-carbon-ink text-bone-parchment" : "border border-dashed border-mist text-mist",
      )}>
        <Icon size={small ? 11 : 13} strokeWidth={2} aria-hidden />
      </span>
      {withLabel && <span className="font-medium">{b.label}</span>}
      {earned && count !== undefined && count > 1 && (
        <span className={cn("tabular-nums text-[10px] font-semibold", withLabel ? "text-ashen" : "absolute -right-[5px] -top-[5px] rounded-full bg-clay px-[4px] leading-[15px] text-paper-white")}>
          {withLabel ? `x${count}` : count}
        </span>
      )}
    </span>
  );
}

export function initials(name: string) {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase()).join("");
}

export function Avatar({ name, me, small }: { name: string; me?: boolean; small?: boolean }) {
  return (
    <span aria-hidden className={cn(
      "flex shrink-0 items-center justify-center rounded-full font-serif",
      small ? "h-[32px] w-[32px] text-[12px]" : "h-[40px] w-[40px] text-[15px]",
      me ? "bg-carbon-ink text-bone-parchment" : "bg-soft-stone text-graphite",
    )}>
      {initials(name)}
    </span>
  );
}

const badgeTotal = (m: WallMember) => m.badges.reduce((a, b) => a + b.count, 0);

function LeaderRow({ m, rank, badges = false }: { m: WallMember; rank: number | null; badges?: boolean }) {
  const kinds = badges ? BADGE_ORDER.filter((k) => m.badges.some((b) => b.kind === k)) : [];
  return (
    <li className={cn("flex items-center gap-[10px] rounded-lg px-8 py-8", m.is_me ? "bg-soft-stone" : "")}>
      <span className="w-[16px] shrink-0 text-right font-serif text-body tabular-nums text-ashen">{rank ?? ""}</span>
      <Avatar name={m.full_name} me={m.is_me} small />
      <div className="min-w-0 flex-1">
        <p className="flex items-center gap-[6px] text-body font-medium text-carbon-ink">
          <span className="truncate">{m.full_name}</span>
          {m.is_me && <span className="shrink-0 text-xs2 font-normal text-ashen">Tú</span>}
          {m.is_me && !m.visible && <EyeOff size={12} className="shrink-0 text-ashen" aria-label="Oculto para tus compañeros" />}
        </p>
        {m.tagline && <p className="truncate text-caption text-ashen" title={m.tagline}>{m.tagline}</p>}
        {kinds.length > 0 && (
          <div className="mt-[4px] flex flex-wrap gap-[4px]">
            {kinds.map((k) => <BadgeChip key={k} kind={k} small count={m.badges.find((b) => b.kind === k)?.count} />)}
          </div>
        )}
      </div>
      <div className="flex shrink-0 flex-col items-end">
        {m.streak.current > 0 ? <StreakNumber value={m.streak.current} size="sm" /> : <span className="text-caption text-ashen">Arrancando</span>}
        {m.streak.week_done && <span className="inline-flex items-center gap-[2px] text-caption text-graphite"><Check size={11} strokeWidth={2.5} /> semana</span>}
      </div>
    </li>
  );
}

/**
 * Rachas del grupo: los tres primeros y, si no estás entre ellos, tu lugar en cuarto renglón.
 * Mismo lugar para la misma racha; quien está en cero no recibe lugar.
 */
export function GroupLeaderboard({ wall }: { wall: Wall }) {
  const [all, setAll] = useState(false);
  const ranked = useMemo(() => {
    const list = [...wall.members].sort((a, b) =>
      b.streak.current - a.streak.current || badgeTotal(b) - badgeTotal(a) || a.full_name.localeCompare(b.full_name, "es"));
    let last = -1, place = 0;
    return list.map((m, i) => {
      if (m.streak.current !== last) { place = i + 1; last = m.streak.current; }
      return { m, rank: m.streak.current > 0 ? place : null };
    });
  }, [wall]);
  const top = ranked.slice(0, 3);
  const meRow = ranked.find((r) => r.m.is_me);
  const meOutside = meRow && !top.includes(meRow);
  const shown = top;
  const done = wall.members.filter((m) => m.streak.week_done).length;
  return (
    <section className="flex flex-col">
      <h2 className="font-serif text-[19px] leading-tight text-carbon-ink">Rachas del grupo</h2>
      <p className="mt-[2px] text-caption text-ashen">{done} de {wall.members.length} ya cumplieron su meta esta semana.</p>
      <ol className="-mx-8 mt-8 flex flex-col">
        {shown.map(({ m, rank }) => <LeaderRow key={m.id} m={m} rank={rank} />)}
        {meOutside && (
          <>
            <li aria-hidden className="px-8 py-[2px] text-center text-caption leading-none text-mist">···</li>
            <LeaderRow m={meRow.m} rank={meRow.rank} />
          </>
        )}
      </ol>
      {ranked.length > 3 && (
        <button onClick={() => setAll(true)} className="mt-8 self-start text-xs2 text-graphite underline underline-offset-4 hover:text-carbon-ink">
          Ver lista completa ({ranked.length})
        </button>
      )}
      <Dialog open={all} onClose={() => setAll(false)} title="Rachas del grupo">
        <p className="-mt-8 text-xs2 text-ashen">{wall.group.name}. {done} de {wall.members.length} ya cumplieron esta semana.</p>
        <ol className="-mx-8 mt-16 flex max-h-[60dvh] flex-col overflow-y-auto">
          {ranked.map(({ m, rank }) => <LeaderRow key={m.id} m={m} rank={rank} badges />)}
        </ol>
      </Dialog>
    </section>
  );
}
